import { useState, useRef } from 'react';
import api from '../api/client';

export function useEtsyUpload(loadProducts) {
  const [showEtsyUploadPopup, setShowEtsyUploadPopup] = useState(false);
  const [selectedEtsyFile, setSelectedEtsyFile] = useState(null);
  const [parsedEtsyData, setParsedEtsyData] = useState([]);
  const [uploadEtsyStatus, setUploadEtsyStatus] = useState(null);
  const [uploadEtsyWeek, setUploadEtsyWeek] = useState('');
  const [uploadEtsyMonth, setUploadEtsyMonth] = useState('');
  const [uploadEtsyYear, setUploadEtsyYear] = useState('');
  const [etsyPeriodMode, setEtsyPeriodMode] = useState('weekly'); // 'weekly' | 'monthly'
  const etsyFileInputRef = useRef(null);

  const handleEtsyFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setSelectedEtsyFile(file);
    setParsedEtsyData([]);
    setUploadEtsyStatus({ type: 'loading', message: 'Parsing Etsy file...' });

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data: result } = await api.post('/api/upload-etsy-test', formData);

      const formattedData = result.preview.map(item => ({
        itemId: item.itemId,
        quantity: item.quantity
      }));
      setParsedEtsyData(formattedData);
      setUploadEtsyStatus({
        type: 'success',
        message: `Successfully parsed ${result.totalRows} rows, aggregated into ${result.uniqueItems} unique Item IDs (Total QTY: ${result.totalQuantity})`
      });
    } catch (error) {
      if (error.response) {
        setUploadEtsyStatus({
          type: 'error',
          message: error.response.data?.error || 'Error parsing Etsy file',
          details: error.response.data?.details
        });
      } else {
        setUploadEtsyStatus({ type: 'error', message: 'Error parsing Etsy file', details: error.message });
      }
    }
  };

  const handleEtsyUploadSubmit = async () => {
    const isWeekly = etsyPeriodMode === 'weekly';

    if (!selectedEtsyFile || !uploadEtsyYear || parsedEtsyData.length === 0) {
      setUploadEtsyStatus({ type: 'error', message: 'Please select a file and fill in all required fields' });
      return;
    }
    if (isWeekly && !uploadEtsyWeek) {
      setUploadEtsyStatus({ type: 'error', message: 'Please enter week number' });
      return;
    }
    if (!isWeekly && !uploadEtsyMonth) {
      setUploadEtsyStatus({ type: 'error', message: 'Please enter month number' });
      return;
    }

    setUploadEtsyStatus({ type: 'loading', message: 'Uploading Etsy data to database...' });

    try {
      const formData = new FormData();
      formData.append('file', selectedEtsyFile);
      formData.append('year', uploadEtsyYear);

      let url;
      if (isWeekly) {
        formData.append('weeknumber', uploadEtsyWeek);
        url = `/api/upload-etsy-sales`;
      } else {
        formData.append('month', uploadEtsyMonth);
        url = `/api/upload-etsy-month-sales`;
      }

      const { data: result } = await api.post(url, formData);

      setUploadEtsyStatus({
        type: 'success',
        message: result.message || 'Etsy data uploaded successfully!',
        details: isWeekly
          ? `Aggregated ${result.originalRows} rows into ${result.aggregatedRows} unique Item IDs. Total Quantity: ${result.stats?.totalQuantity}`
          : `Processed ${result.stats?.totalRows} rows.`
      });
      setTimeout(() => {
        if (loadProducts) loadProducts();
        closeEtsyUploadPopup();
      }, 2000);
    } catch (error) {
      if (error.response) {
        setUploadEtsyStatus({
          type: 'error',
          message: error.response.data?.error || 'Error uploading Etsy data',
          details: error.response.data?.details
        });
      } else {
        setUploadEtsyStatus({ type: 'error', message: 'Error uploading Etsy data', details: error.message });
      }
    }
  };

  const closeEtsyUploadPopup = () => {
    setShowEtsyUploadPopup(false);
    setSelectedEtsyFile(null);
    setParsedEtsyData([]);
    setUploadEtsyStatus(null);
    setUploadEtsyWeek('');
    setUploadEtsyMonth('');
    setUploadEtsyYear('');
    if (etsyFileInputRef.current) etsyFileInputRef.current.value = '';
  };

  return {
    showEtsyUploadPopup, setShowEtsyUploadPopup,
    selectedEtsyFile, setSelectedEtsyFile,
    parsedEtsyData, setParsedEtsyData,
    uploadEtsyStatus, setUploadEtsyStatus,
    uploadEtsyWeek, setUploadEtsyWeek,
    uploadEtsyMonth, setUploadEtsyMonth,
    uploadEtsyYear, setUploadEtsyYear,
    etsyPeriodMode, setEtsyPeriodMode,
    etsyFileInputRef,
    handleEtsyFileUpload,
    handleEtsyUploadSubmit,
    closeEtsyUploadPopup
  };
}